import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';

import { useIntl } from 'react-intl';
import { connect } from 'react-redux';
import { getProducts, addProductRelatedProducts, deleteProductRelatedProducts } from '../../../../redux/methods/products';
import { selectStoreCurrentLanguage } from '../../../../redux/selectors/storeLocale';
import useFetch from '../../../../hooks/useFetch';
import useSearchConfig from '../../../../hooks/useSearchConfig';
import { translateProductName } from '../../../../utils/products';

import { Button, Container } from 'reactstrap';

import ProductWithDeleteActionTable from '../../../../components/tables/ProductWithDeleteActionTable';
import ProductSearchModal from '../../ProductSearchModal';
import Paginator from '../../../../components/Paginator';
import CheckboxWithTooltip from '../../../../components/CheckboxWithTooltip';

import { stringOrNumberPropTypes } from '../../../../propTypes';
import { productsSearchConfig } from '../../../../config/searchConfigs';

import styles from './index.module.scss';

import { withFormUtils } from '../../../../hoc/withFormUtils';

const propTypes = {
    totalPages: PropTypes.number,
    currentPage: PropTypes.number,
    getProducts: PropTypes.func,
    addProductRelatedProducts: PropTypes.func,
    id: stringOrNumberPropTypes,
    deleteProductRelatedProducts: PropTypes.func,
    storeCurrentLanguage: PropTypes.string,
    forceRefetch: PropTypes.bool,
};

const ProductEditRelatedProducts = ({
    utils, getProducts, id, addProductRelatedProducts,
    deleteProductRelatedProducts, forceRefetch, storeCurrentLanguage,
}) => {
    const intl = useIntl();
    const translations = intl.messages.products.edit.tabs.relatedProducts;

    const [checkedProductsId, setCheckedProductsId] = useState([]);
    const [checkedProductsToAssignId, setCheckedProductsToAssignId] = useState([]);
    const [isSearchModalOpen, setIsSearchModalOpen] = useState(false);
    const [ isTwoWayOperation, setIsTwoWayOperation ] = useState(false);

    const [ searchConfig, changePage ] = useSearchConfig({ ...productsSearchConfig, productRelatedId: id });

    const [ products, pages ] = useFetch(getProducts, searchConfig, [searchConfig], forceRefetch);

    const translatedProducts = products && translateProductName(products, storeCurrentLanguage);

    useEffect(() => {
        setCheckedProductsId([]);
        setCheckedProductsToAssignId([]);
        setIsSearchModalOpen(false);
    }, [products]);

    const handleRemoveSingle = idToRemove => {
        deleteProductRelatedProducts([idToRemove], id, isTwoWayOperation);
    };

    const handleOpenDeleteModal = () => {
        utils.confirm(
            () => deleteProductRelatedProducts(checkedProductsId, id, isTwoWayOperation),
            translations.deleteManyModal.header,
            translations.deleteManyModal.body
        );
    };

    const handleOpenProductSearchModal = () => {
        setIsSearchModalOpen(true);
    };

    const handleAssignProducts = () => {
        addProductRelatedProducts(checkedProductsToAssignId, id, isTwoWayOperation);
    };

    const handleIsTwoWayChecbox = () => {
        setIsTwoWayOperation(!isTwoWayOperation);
    };

    return products ? (
        <Container fluid>
            <ProductWithDeleteActionTable
                translatedProductsList={translatedProducts}
                checkedProductsId={checkedProductsId}
                setCheckedProductsId={setCheckedProductsId}
                removeAction={handleRemoveSingle}
                deleteModal={translations.deleteModal}
                tooltipTranslation={translations.tooltips}
                fillers={translations.fallbacks}
                emptyTable={translations.empty}
            />
            {
                pages > 1 &&
                    <Paginator
                        totalPages={pages}
                        currentPage={searchConfig.curentPage + 1}
                        onPageChange={changePage}
                    />
            }
            <div className='my-2'>
                <CheckboxWithTooltip
                    label={translations.labels.isTwoWay}
                    inputProps={{
                        name: 'twoWay',
                        value: isTwoWayOperation,
                        onChange: handleIsTwoWayChecbox

                    }}
                    tooltipText={translations.tooltips.isTwoWay}
                />
            </div>
            <aside className={styles.footer}>
                <Button outline type='button' onClick={handleOpenDeleteModal} color='danger' disabled={!checkedProductsId.length > 0}>
                    <i className='fa fa-remove mr-2'/>
                    <span>{translations.buttons.delete}</span>
                </Button>
                <Button color='primary' onClick={handleOpenProductSearchModal}>
                    <i className='fa fa-plus mr-2'/>
                    <span>{translations.buttons.add}</span>
                </Button>
            </aside>
            <ProductSearchModal
                isOpen={isSearchModalOpen}
                setIsOpen={setIsSearchModalOpen}
                checkedProductsId={checkedProductsToAssignId}
                setCheckedProductsId={setCheckedProductsToAssignId}
                onConfirm={handleAssignProducts}
            />
        </Container>
    ) : null;
};

ProductEditRelatedProducts.propTypes = propTypes;

const mapStateToProps = state => ({
    storeCurrentLanguage: selectStoreCurrentLanguage(state),
    forceRefetch: state.refetchers.productsRelated,
});

const mapDispatch = {
    getProducts,
    addProductRelatedProducts,
    deleteProductRelatedProducts,
};

export default withFormUtils(connect(mapStateToProps, mapDispatch)(ProductEditRelatedProducts));
